import { FC, createContext, ReactNode, PropsWithChildren, useContext, useMemo, useState } from 'react';

import { SanitizedSearchParams } from './SanitizedSearchParams';

export type Locale = 'nl-NL' | 'en-NL' | 'nl-BE' | 'fr-BE' | 'en-GB';
export type Language = 'nl' | 'en' | 'fr';
export type CountryCode = 'NL' | 'BE' | 'GB';
export type I18n = {
  language: Language;
  languages: Language[];
  locale: Locale;
  locales: Locale[];
  defaultLocale?: Locale;
  countryCode: CountryCode;
  setLocale?: (locale: Locale) => void;
};

type ApplicationProviderContext = I18n & {
  searchParams: URLSearchParams;
  isEditMode: boolean | undefined;
};

// TODO: Find another solution for default value locale
const ApplicationContext = createContext<ApplicationProviderContext>({
  locale: 'nl-NL',
  locales: ['nl-NL'],
  language: 'nl',
  languages: ['nl'],
  defaultLocale: 'nl-NL',
  countryCode: 'NL',
  searchParams: new URLSearchParams(),
  isEditMode: false,
});

type Props = {
  path?: string;
  i18n: I18n;
  children: ReactNode;
  isEditMode?: boolean;
};

/**
 * Custom authorize query parameter for Eneco BE Okta-hosted screens (`language=nl` | `language=fr`).
 */
export function localeToOktaLanguage(locale: Locale): 'nl' | 'fr' {
  if (locale === 'fr-BE') {
    return 'fr';
  }
  return 'nl';
}

export const useApplication = (): ApplicationProviderContext => useContext(ApplicationContext);

export const ApplicationProvider: FC<PropsWithChildren<Props>> = ({ children, path, i18n, isEditMode }) => {
  const [locale, setLocale] = useState<Locale>(i18n.locale);
  const language = locale.split('-')[0] as Language;
  const searchParams = useMemo(() => new SanitizedSearchParams(path?.replace(/^[^?]*/, '')), [path]);

  const contextValue = useMemo(
    () => ({ ...i18n, locale, setLocale, language, searchParams, isEditMode }),
    [i18n, locale, setLocale, language, searchParams, isEditMode],
  );

  return <ApplicationContext.Provider value={contextValue}>{children}</ApplicationContext.Provider>;
};
